import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FEATURES = [
  {
    icon: "ri-film-line",
    title: "海量影视资源",
    desc: "聚合电影、电视剧等多类型高清影视内容，覆盖院线新片与口碑经典。",
  },
  {
    icon: "ri-search-eye-line",
    title: "高效检索体验",
    desc: "支持关键词搜索与地区、年份、类型多维筛选，快速定位想看的内容。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯一站聚合",
    desc: "影视新闻、行业动态、深度影评与预告片，第一时间掌握圈内动向。",
  },
  {
    icon: "ri-heart-line",
    title: "个性化收藏",
    desc: "喜欢的作品一键收藏，浏览器本地保存，下次打开依然还在。",
  },
  {
    icon: "ri-device-line",
    title: "多端流畅适配",
    desc: "桌面与手机端均精心适配，随时随地享受清爽的浏览体验。",
  },
  {
    icon: "ri-shield-check-line",
    title: "清爽无干扰",
    desc: "界面简洁、层级清晰，专注内容本身，让观影决策更轻松。",
  },
];

const VALUES = [
  { num: "01", title: "以用户为中心", desc: "从查找、浏览到观看，每个环节都力求简单直接，减少不必要的操作。" },
  { num: "02", title: "内容优先", desc: "持续扩充优质影视与资讯内容，让每一次访问都有新的发现。" },
  { num: "03", title: "持续打磨", desc: "不断优化界面与体验，用细节的积累带来更舒适的观影感受。" },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "意见反馈邮箱", value: "feedback@xigua-video.com" },
  { icon: "ri-customer-service-2-line", label: "用户支持邮箱", value: "support@xigua-video.com" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区影视文化产业园 A 座" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周日 09:00 - 21:00" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 西瓜影音",
    description:
      "了解西瓜影音：一个面向影视爱好者的高清影视内容与资讯聚合平台，致力于提供高效查找、清爽浏览与流畅观影的体验。",
    keywords: "关于西瓜影音,影视平台介绍,联系我们,意见反馈",
  });

  return (
    <div className="animate-fade-in">
      {/* 顶部介绍 */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20abstract%20artistic%20collage%20of%20film%20and%20cinema%20theme%20with%20warm%20orange%20light%20deep%20dark%20toned%20background%20atmospheric%20glow%20high%20detail%20editorial%20composition%20with%20dark%20space%20for%20text&width=1600&height=600&seq=wm-about-hero&orientation=landscape"
            alt="西瓜影音关于我们背景"
            title="西瓜影音 高清影视平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/80 to-background-950/60"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 py-16 md:py-24">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-5" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-200">关于我们</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 mb-5 max-w-3xl leading-tight">
            关于西瓜影音
          </h1>
          <p className="text-base md:text-lg text-foreground-200 leading-relaxed max-w-3xl">
            西瓜影音是一个面向广大影视爱好者的高清影视内容与资讯聚合平台。我们希望用清爽的界面和高效的方式，
            帮你在海量影视内容里快速找到想看的作品，并随时了解影视圈的最新动态。
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link
              to="/category/movie"
              className="px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              开始浏览影视
            </Link>
            <Link
              to="/news"
              className="px-6 py-3 rounded-md bg-background-950/60 border border-background-600 hover:border-primary-500 text-foreground-100 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              查看影视资讯
            </Link>
          </div>
        </div>
      </section>

      {/* 定位介绍 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-950">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-primary-400 text-sm font-medium mb-4">
              <i className="ri-water-flash-line"></i>
              我们的定位
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-5 leading-tight">
              让每一次观影选择都更轻松
            </h2>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
              我们相信，好的观影体验不只在于内容本身，也在于发现它的过程是否足够愉悦。
              西瓜影音正是为此而生——用清晰的分类、精准的筛选和直观的展示，把「找片」这件事变得简单。
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
              无论是想追一部热播剧，还是想找一部高分老片慢慢回味，这里都能帮你更快抵达。
            </p>
          </div>
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-800">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20home%20theater%20room%20with%20warm%20orange%20screen%20glow%20and%20cozy%20seating%20atmospheric%20high%20detail%20editorial%20photography%20with%20dark%20tones&width=1000&height=750&seq=wm-about-pos&orientation=landscape"
              alt="西瓜影音观影场景"
              title="西瓜影音 高清在线观看"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* 我们提供什么 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-900">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
            我们提供什么
          </h2>
          <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto">
            从内容到体验，围绕影视爱好者最真实的需求设计
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="rounded-lg bg-background-800/60 border border-background-800 p-6 hover:border-primary-500/50 transition-colors"
            >
              <span className="w-12 h-12 rounded-lg bg-primary-500/15 flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-2xl text-primary-400`}></i>
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-2.5">
                {feature.title}
              </h3>
              <p className="text-sm text-foreground-400 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 服务理念 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-950">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {VALUES.map((value) => (
            <div key={value.num} className="rounded-lg bg-background-900 border border-background-800 p-6 md:p-8">
              <span className="font-heading text-4xl font-bold text-accent-500/80">{value.num}</span>
              <h3 className="font-heading text-xl font-semibold text-foreground-50 mt-4 mb-3">
                {value.title}
              </h3>
              <p className="text-sm text-foreground-400 leading-relaxed">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系我们 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-primary-400 text-sm font-medium mb-4">
              <i className="ri-customer-service-line"></i>
              联系我们
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-5 leading-tight">
              你的每一条建议，我们都很在意
            </h2>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
              如果你在使用过程中遇到问题，或对内容与功能有任何建议，欢迎通过以下方式与我们取得联系。
            </p>
            <div className="rounded-lg bg-background-950/60 border border-background-800 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-100 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-400"></i>
                意见反馈说明
              </h3>
              <ul className="space-y-2.5 text-sm text-foreground-400">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-400 mt-0.5"></i>
                  请将问题描述、涉及页面与截图发送至意见反馈邮箱，方便我们快速定位。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-400 mt-0.5"></i>
                  内容纠错类反馈请注明影片或资讯名称，我们会尽快核实处理。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-primary-400 mt-0.5"></i>
                  我们通常会在 1-3 个工作日内回复，节假日可能稍有延迟，敬请谅解。
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CONTACTS.map((contact) => (
              <div key={contact.label} className="rounded-lg bg-background-950/60 border border-background-800 p-5">
                <span className="w-10 h-10 rounded-lg bg-primary-500/15 flex items-center justify-center mb-3">
                  <i className={`${contact.icon} text-xl text-primary-400`}></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1.5">{contact.label}</p>
                <p className="text-sm text-foreground-200 break-all">{contact.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}